import polylineUtil from '@mapbox/polyline';
import {
  featureCollection as toFeatureCollection,
  point as toPointFeature,
  multiLineString as toMultiLineStringFeature,
  lineString as toLineString,
  distance as distanceBetween,
  bearing,
  destination
} from "@turf/turf";
import { isFinite, isEqual, pick, cloneDeep, concat } from "lodash";

const VALHALLA_USES_INDICATING_NARROW_PATHES = [
  "path",
  "footway",
  "cycleway",
  "alley",
  "mountain_bike"
];

function polylineToLineStringFeature(polyline) {
  const feature = {
    type: 'Feature',
    properties: { encodedPolyline: polyline },
    geometry: {
      coordinates: polylineUtil.decode(polyline).map(coord => coord.reverse()),
      type: "LineString"
    }
  };
  return feature;
}

function lineStringFeatureSurfaceCharacter(lineString) {
  const { surface, use, unpaved } = lineString.properties;
  let result = {};

  if (surface == "path" || VALHALLA_USES_INDICATING_NARROW_PATHES.includes(use)) {
    result.width = "narrow";
  } else {
    result.width = "wide";
  }

  if (surface == "gravel") {
    result.surface = "gravel";
  } else if (unpaved) {
    result.surface = "dirt";
  } else {
    result.surface = "paved";
  }

  return result;
}

function destinationBetweenPoints({ startPoint, endPoint, distance }) {
  const directionBearing = bearing(startPoint, endPoint);
  const destinationPoint = destination(startPoint, distance, directionBearing);

  return destinationPoint.geometry.coordinates;
}

function convertToMultiLineString({ lineStringFeature, distancePerSegment, initialSegmentOffset = 0 }) {
  if (!isFinite(distancePerSegment) || !isFinite(initialSegmentOffset)) {
    throw "distancePerSegment and initialSegmentOffset must be numbers";
  }
  if (distancePerSegment <= initialSegmentOffset) {
    throw "distancePerSegment must be greater than initialSegmentOffset";
  }

  const distanceThreshold = 10/1000; // consider anything <= 10 meters to suffice as equal
  const segments = [];
  const path = lineStringFeature.geometry.coordinates.slice();
  let pathIndex = 1, segIndex = 0, distanceAccumulator = initialSegmentOffset;

  segments.push([path[0]]); // Push first segment with starting point

  do {
    const delta = distanceBetween(toPointFeature(path[pathIndex - 1]), toPointFeature(path[pathIndex]));
    const distanceDifference = Math.abs(distanceAccumulator + delta - distancePerSegment);
    if (distanceDifference > distanceThreshold) {
      if (distanceAccumulator + delta < distancePerSegment) {
        // Need to do more loops before creating a new segment
        segments[segIndex].push(path[pathIndex]);
        distanceAccumulator += delta;
        pathIndex++;
      } else {
        // Need to add a new point
        const newPoint = destinationBetweenPoints({
          startPoint: toPointFeature(path[pathIndex - 1]),
          endPoint: toPointFeature(path[pathIndex]),
          distance: distancePerSegment - distanceAccumulator
        });
        path.splice(pathIndex, 0, newPoint);
      }
    } else {
      // We should make a new segment
      segments[segIndex].push(path[pathIndex]);
      pathIndex++;
      // Move to the next segment is appropriate
      if (pathIndex < path.length) {
        const endOfLastSegment = path[pathIndex - 1];
        segments.push([endOfLastSegment]);
        segIndex++;
        distanceAccumulator = 0;
      }
    }
  } while (pathIndex < path.length);

  return toMultiLineStringFeature(segments);
}

function convertToLineStringFeatures({ multiLineStringFeature }) {
  if (multiLineStringFeature.geometry.type != "MultiLineString"){
    throw `Must pass MultiLineString feature, got ${multiLineStringFeature.geometry.type}`;
  }

  return multiLineStringFeature.geometry.coordinates.map(
    (coordinates) => toLineString(coordinates, multiLineStringFeature.properties)
  );
}

function filterFeatureCollection({ featureCollection, types }) {
  const features = featureCollection.features.filter((feature) => {
    return types.includes(feature.geometry.type);
  });

  return toFeatureCollection(features);
}

function compactLineStringsByProperties(featureCollection, propKeys) {
  const result = [];
  let compactFeature = null;

  featureCollection.features.forEach((feature) => {
    if ("LineString" == feature.geometry.type) {
      if (compactFeature === null) {
        compactFeature = cloneDeep(feature);
        return;
      }

      if (
        isEqual(
          pick(compactFeature.properties, propKeys),
          pick(feature.properties, propKeys)
        )
      ) {
        compactFeature.geometry.coordinates = concat(
          compactFeature.geometry.coordinates,
          feature.geometry.coordinates.slice(1)
        );
      } else {
        result.push(cloneDeep(compactFeature));
        compactFeature = cloneDeep(feature);
      }
    } else if (feature.geometry.type == "MultiLineString") {
      throw new Error("does not support MultiLineString");
    } else {
      result.push(cloneDeep(feature));
    }
  });
  result.push(cloneDeep(compactFeature));

  return toFeatureCollection(result);
}

export {
  compactLineStringsByProperties,
  polylineToLineStringFeature,
  lineStringFeatureSurfaceCharacter,
  filterFeatureCollection,
  convertToMultiLineString,
  convertToLineStringFeatures
};
